import { auth } from '../firebase/config';
import { WaitlistAccountEntry, WaitlistEventStatusResponse } from '../types';

/**
 * Joins the customer has made, kept outside the query cache and layered back
 * on every read of it.
 *
 * The server only learns about a join when the account next syncs, so any
 * refetch between the join and that sync answers "not on the list" — truthfully
 * for its own data, and wrongly for the customer, who watched the badge appear
 * and then vanish. A cache write alone cannot survive that; nor can memory
 * survive a reload, which is why this is mirrored to localStorage.
 *
 * Module scope on purpose: it has to outlive the picker unmounting, and every
 * hook reading event status reads the same one.
 */
type OptimisticJoin = { entry: WaitlistAccountEntry; at: number };

/**
 * How long a join is believed without confirmation. Generous because the only
 * thing that retires it early is a sync, and the alternative — forgetting —
 * tells a customer who just joined that they did not.
 */
export const JOIN_MEMORY_TTL_MS = 6 * 60 * 60 * 1000;
const JOIN_MEMORY_KEY_PREFIX = 'flashtools.waitlistJoins.v1';

/**
 * Storage is per browser, so the key is per user.
 *
 * Without this, two people sharing a machine share a bucket, and an account id
 * that appears in both their event-status responses would show one of them a
 * badge earned by the other. Nothing in the payloads guarantees those ids are
 * disjoint, and "probably disjoint" is not a property to hang a data leak on.
 */
const storageKeyFor = (uid: string | null) =>
  uid ? `${JOIN_MEMORY_KEY_PREFIX}.${uid}` : `${JOIN_MEMORY_KEY_PREFIX}.anon`;

/** Overridable so tests can drive the user without booting Firebase. */
let currentUid: () => string | null = () => auth.currentUser?.uid ?? null;

/** Test seam. Not used by the app. */
export const setJoinMemoryUidProvider = (provider: () => string | null) => {
  currentUid = provider;
  loadedFor = UNLOADED;
};

const readStored = (uid: string | null): Map<string, Map<number, OptimisticJoin>> => {
  const store = new Map<string, Map<number, OptimisticJoin>>();
  try {
    const raw = localStorage.getItem(storageKeyFor(uid));
    if (!raw) return store;
    const parsed = JSON.parse(raw) as Record<string, Record<string, OptimisticJoin>>;
    const cutoff = Date.now() - JOIN_MEMORY_TTL_MS;
    for (const [eventId, accounts] of Object.entries(parsed ?? {})) {
      const overlay = new Map<number, OptimisticJoin>();
      for (const [accountId, join] of Object.entries(accounts ?? {})) {
        if (join?.entry && typeof join.at === 'number' && join.at > cutoff) {
          overlay.set(Number(accountId), join);
        }
      }
      if (overlay.size > 0) store.set(eventId, overlay);
    }
  } catch {
    // Unreadable or disabled storage just means no remembered joins.
  }
  return store;
};

/** A uid can legitimately be null (signed out), so "never loaded" needs its
 *  own value rather than borrowing null. */
const UNLOADED = Symbol('unloaded');
let loadedFor: string | null | typeof UNLOADED = UNLOADED;
let joins = new Map<string, Map<number, OptimisticJoin>>();

/**
 * The current user's joins, reloaded when the user changes.
 *
 * Lazy rather than read at import: this module is imported before Firebase has
 * restored the session, so an eager read would always land in the signed-out
 * bucket and then write to a different one.
 */
const memory = () => {
  const uid = currentUid();
  if (loadedFor !== uid) {
    joins = readStored(uid);
    loadedFor = uid;
  }
  return joins;
};

const persist = () => {
  try {
    const plain: Record<string, Record<number, OptimisticJoin>> = {};
    joins.forEach((overlay, eventId) => {
      if (overlay.size === 0) return;
      const accounts: Record<number, OptimisticJoin> = {};
      overlay.forEach((join, accountId) => {
        accounts[accountId] = join;
      });
      plain[eventId] = accounts;
    });
    localStorage.setItem(
      storageKeyFor(loadedFor === UNLOADED ? currentUid() : loadedFor),
      JSON.stringify(plain),
    );
  } catch {
    // Storage full or unavailable — the in-memory overlay still works for this
    // session, so there is nothing useful to do here.
  }
};

/** Records confirmed joins so they survive refetches and reloads. */
export const rememberJoins = (
  eventId: string,
  entries: Array<{ accountId: number; entry: WaitlistAccountEntry }>,
  at: number = Date.now(),
) => {
  const store = memory();
  const overlay = store.get(eventId) ?? new Map<number, OptimisticJoin>();
  for (const { accountId, entry } of entries) {
    overlay.set(Number(accountId), { entry, at });
  }
  store.set(eventId, overlay);
  persist();
};

/** Drops remembered joins — for a claim that turned out to be wrong. */
export const forgetJoins = (eventId: string, accountIds: number[]) => {
  const overlay = memory().get(eventId);
  if (!overlay) return;
  accountIds.forEach((id) => overlay.delete(Number(id)));
  persist();
};

/** Test seam. Not used by the app. */
export const clearJoinMemory = () => {
  memory().clear();
  persist();
};

/**
 * Reapplies remembered joins to a server response.
 *
 * A remembered join is retired only by evidence that beats it: an entry the
 * server has actually seen since the join, or the TTL running out. Note that a
 * pre-existing entry does NOT retire it — an account can hold a months-old
 * timed-out row whose `last_synced_at` is older than the join, and that row
 * must not shadow what the customer just did.
 */
export const applyRememberedJoins = (
  data: WaitlistEventStatusResponse,
  /**
   * The event the caller asked about. Defaults to the id in the response, but
   * callers that know it should pass it: joins are recorded under the id we
   * sent, and matching on the echo makes the whole overlay depend on the server
   * returning that id in exactly the same form.
   */
  eventId: string = data.event_id,
): WaitlistEventStatusResponse => {
  const overlay = memory().get(eventId);
  if (!overlay || overlay.size === 0) return data;

  const now = Date.now();
  let retired = false;
  const accounts = data.accounts.map((account) => {
    // Coerced because the id round-trips through JSON on the way into storage
    // and arrives from two different endpoints; a string "38" missing a
    // numeric 38 would drop the badge on reload and nowhere else.
    const pending = overlay.get(Number(account.account_id));
    if (!pending) return account;

    if (now - pending.at > JOIN_MEMORY_TTL_MS) {
      overlay.delete(account.account_id);
      retired = true;
      return account;
    }

    // Once the sync catches up, the server's own row is the better answer — it
    // carries the tier and quantity Dice actually recorded.
    //
    // A timed-out row does NOT count as catching up, however fresh its
    // timestamp. Accounts routinely carry an old expired entry, and syncs
    // restamp last_synced_at whether or not anything changed — so treating
    // that as confirmation retires a join we just made and puts the badge back
    // to nothing until the next real sync.
    //
    // Our own optimistic row is not evidence either. markWaitlistJoined writes
    // the join into the query cache stamped source: 'flashtools' and
    // last_synced_at: now, and `select` runs over that cache — so without this
    // the memory confirms itself out of existence the instant it is written,
    // leaving the badge alive only as long as the cache that fed it.
    const synced = account.waitlist;
    const confirmsTheJoin =
      !!synced &&
      synced.source !== 'flashtools' &&
      synced.status !== 'timed-out' &&
      Date.parse(synced.last_synced_at) >= pending.at;
    if (confirmsTheJoin) {
      overlay.delete(account.account_id);
      retired = true;
      return account;
    }

    return { ...account, waitlist: pending.entry };
  });

  if (retired) persist();
  return { ...data, accounts };
};

/**
 * Dev-only window handle for inspecting what is remembered.
 *
 * Reaching for localStorage by hand means knowing the key, which is now
 * per-user; this prints the key in use alongside the contents.
 */
if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  (window as any).__waitlistJoins = {
    key: () => storageKeyFor(currentUid()),
    uid: () => currentUid(),
    dump: () => {
      const out: Record<string, Record<number, OptimisticJoin>> = {};
      memory().forEach((overlay, eventId) => {
        const accounts: Record<number, OptimisticJoin> = {};
        overlay.forEach((join, accountId) => {
          accounts[accountId] = join;
        });
        out[eventId] = accounts;
      });
      return out;
    },
    raw: () => localStorage.getItem(storageKeyFor(currentUid())),
  };
}
